import { Database, ref, set, get, push, update } from 'firebase/database';
import { FormValues } from '../../components/case/CaseForm';
import { CaseType } from '../../stateMachines/submitCase/caseType';
import { PaymentType } from '../../stateMachines/submitCase/selectPaymentType';
import { Site } from '../buildfire/rdb/cases';

export interface CaseDraft {
  id?: string;
  caseType: CaseType;
  caseInfo: FormValues;
  paymentType: PaymentType;
  selectedPaymentMethod?: string;
  selectedInvoiceAccountUid?: string;
  createdAt: number;
  updatedAt: number;
}

function clearUndefined<T extends {}>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  ) as T;
}

function normalizeDraftSites(draft: CaseDraft): CaseDraft {
  return {
    ...draft,
    caseInfo: {
      ...draft.caseInfo,
      sites: (draft.caseInfo.sites || []).map((site) => ({
        ...site,
        images: site.images || [],
      })),
    },
  };
}

export const saveDraft = async (
  database: Database,
  userId: string,
  draft: Omit<CaseDraft, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const now = Date.now();
  const sites = draft.caseInfo.sites as Site[]; // site images should already be uploaded by this point
  const draftData: Omit<CaseDraft, 'id'> = clearUndefined({
    ...draft,
    updatedAt: now,
    createdAt: now,
  });
  draftData.caseInfo = clearUndefined(draftData.caseInfo);
  draftData.caseInfo.sites = [...sites.map(clearUndefined)] as Site[];

  const draftsRef = ref(database, `drafts/${userId}`);
  const newDraftRef = push(draftsRef, draftData);

  if (!newDraftRef.key) {
    throw new Error('Failed to create draft');
  }

  return newDraftRef.key;
};

export const updateDraft = async (
  database: Database,
  userId: string,
  draftId: string,
  draft: Omit<CaseDraft, 'id' | 'createdAt' | 'updatedAt'>
): Promise<void> => {
  const now = Date.now();
  const sites = draft.caseInfo.sites as Site[]; // site images should already be uploaded by this point
  const draftData: Omit<CaseDraft, 'id'> = clearUndefined({
    ...draft,
    updatedAt: now,
    createdAt: now,
  });
  draftData.caseInfo = clearUndefined(draftData.caseInfo);
  draftData.caseInfo.sites = [...sites.map(clearUndefined)] as Site[];

  const draftRef = ref(database, `drafts/${userId}/${draftId}`);
  await set(draftRef, draftData);
};

export const getDraft = async (
  database: Database,
  userId: string
): Promise<CaseDraft | null> => {
  const draftsRef = ref(database, `drafts/${userId}`);
  const snapshot = await get(draftsRef);

  if (!snapshot.exists()) {
    return null;
  }

  const drafts = snapshot.val();
  const draftEntries = Object.entries(
    drafts as Record<string, Omit<CaseDraft, 'id'>>
  );

  if (draftEntries.length === 0) {
    return null;
  }

  // Return the most recently updated draft
  const [draftId, draftData] = draftEntries.reduce(
    ([maxId, maxDraft], [id, draft]) => {
      return draft.updatedAt > maxDraft.updatedAt
        ? [id, draft]
        : [maxId, maxDraft];
    }
  );

  return normalizeDraftSites({
    id: draftId,
    ...draftData,
  } as CaseDraft);
};

export const getDraftById = async (
  database: Database,
  userId: string,
  draftId: string
): Promise<CaseDraft | null> => {
  const draftRef = ref(database, `drafts/${userId}/${draftId}`);
  const snapshot = await get(draftRef);

  if (!snapshot.exists()) {
    return null;
  }

  const draftData = snapshot.val();
  return normalizeDraftSites({
    id: draftId,
    ...draftData,
  } as CaseDraft);
};

export const deleteDraft = async (
  database: Database,
  userId: string,
  draftId: string
): Promise<void> => {
  const draftRef = ref(database, `drafts/${userId}/${draftId}`);
  await set(draftRef, null);
};

export const updateDraftImages = async (
  database: Database,
  userId: string,
  draftId: string,
  siteIndex: number,
  imageIndex: number,
  imageUrl: string
): Promise<void> => {
  const now = Date.now();
  const imagePath = `drafts/${userId}/${draftId}/caseInfo/sites/${siteIndex}/images/${imageIndex}`;
  const updatedAtPath = `drafts/${userId}/${draftId}/updatedAt`;

  const updates: Record<string, any> = {
    [imagePath]: imageUrl,
    [updatedAtPath]: now,
  };

  await update(ref(database), updates);
};

export const updateDraftSite = async (
  database: Database,
  userId: string,
  draftId: string,
  siteIndex: number,
  site: Site
): Promise<void> => {
  const now = Date.now();
  const sitePath = `drafts/${userId}/${draftId}/caseInfo/sites/${siteIndex}`;
  const updatedAtPath = `drafts/${userId}/${draftId}/updatedAt`;

  const updates: Record<string, any> = {
    [sitePath]: clearUndefined(site),
    [updatedAtPath]: now,
  };

  await update(ref(database), updates);
};
